import React from 'react';
import { Compass, FolderPlus, Layers, ListChecks, Menu } from 'lucide-react';
import { Loader } from '../../../components/ui';
import { cn } from '../../../lib/utils';
import { Project } from '../../../hooks/queries/projectTypes';
import { ProjectCreationModal } from '../ProjectCreationModal';
import { MobileDrawer } from '../../mobile/MobileDrawer';
import { useCreateProject } from '../../../hooks/queries/useProjects';
import { handleNewProjectClick } from '../../../lib/project-utils';
import { useToastUIStore } from '../../../store/toastUIStore';

interface ActiveWelcomeScreenProps {
  hasProjects: boolean;
  latestProject?: Project;
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
  onProjectSelect?: (projectId: string) => void;
  loading: boolean;
  user: any;
  navigate: any;
}

export function ActiveWelcomeScreen({
  hasProjects,
  latestProject,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  onProjectSelect,
  loading,
  user,
  navigate,
}: ActiveWelcomeScreenProps) {
  const [isNewProjectModalOpen, setIsNewProjectModalOpen] = React.useState(false);
  const [isCreating, setIsCreating] = React.useState(false);
  const createProjectMutation = useCreateProject();
  const { addToast } = useToastUIStore();

  const handleCreateProject = async (
    name: string,
    description: string,
    context: string,
    avatarFile?: File
  ) => {
    if (isCreating) return;

    setIsCreating(true);
    try {
      const project = await createProjectMutation.mutateAsync({
        id: crypto.randomUUID(),
        name,
        description,
        context,
        avatarFile
      });
      
      if (project) {
        setIsNewProjectModalOpen(false);
        onProjectSelect?.(project.id);
        addToast({
          type: 'success',
          title: 'Project created',
          message: 'Your new project has been created successfully',
        });
      }
    } catch (error) {
      console.error('Error creating project:', error);
      addToast({
        type: 'error',
        title: 'Project creation failed',
        message: 'Failed to create project. Please try again.',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateNewProject = async () => {
    // Set loading state immediately when button is clicked
    setIsCreating(true);

    try {
      const canCreate = await handleNewProjectClick(user, navigate);
      if (canCreate) {
        setIsNewProjectModalOpen(true);
      }
    } catch (error) {
      console.error('Error checking project limits:', error);
    } finally {
      // Turn off loading state once we've either opened the modal or encountered an error
      setIsCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-400">
          There's no elevator to success, you have to take the stairs.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Mobile Menu Button - Fixed position */}
      <button
        onClick={() => setIsMobileMenuOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-[40] p-2 bg-white rounded-lg shadow-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
      >
        <Menu size={24} />
      </button>

      {/* Mobile Drawer */}
      <MobileDrawer
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        projectId=""
        onProjectSelect={id => {
          onProjectSelect?.(id);
          setIsMobileMenuOpen(false);
        }}
      />

      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center max-w-md mx-auto">
          {/* Icon Group */}
          <div className="relative mb-8">
            {/* Main icon with glow effect */}
            <div className="relative w-24 h-24 mx-auto">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl opacity-75 blur-lg transform transition-all duration-200 group-hover:scale-110" />
              {/* Main icon */}
              <div className="relative w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-[0_8px_16px_rgba(59,91,219,0.2)] transform transition-all duration-200 hover:scale-110 hover:shadow-[0_12px_24px_rgba(59,91,219,0.3)]">
                {loading ? (
                  <Loader color="text-white" />
                ) : (
                  <Compass className="w-12 h-12 text-white" />
                )}
              </div>
              {/* Decorative icons - positioned closer to main icon */}
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center transform rotate-12 shadow-sm">
                <Layers className="w-4 h-4 text-yellow-600" />
              </div>
              <div className="absolute -bottom-2 -left-2 w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center transform -rotate-12 shadow-sm">
                <ListChecks className="w-4 h-4 text-green-600" />
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-4 font-sora">
            {loading
              ? 'Loading Projects...'
              : hasProjects
                ? 'Select a Project'
                : 'Create Your First Project'}
          </h2>

          {!loading && (
            <>
              <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                {hasProjects
                  ? 'Choose a project from the side menu, continue your last opened project or create a new one.'
                  : "Create your first project to start collaborating with our AI assistant. We'll help you plan, organize, and execute your ideas effectively."}
              </p>

              <div
                className="flex flex-col gap-4 animate-fade-in-up"
                style={{ animationDelay: '200ms' }}
              >
                {latestProject && (
                  <>
                    <div className="text-left text-lg text-gray-600 mb-1">Last opened:</div>
                    <button
                      onClick={() => onProjectSelect?.(latestProject.id)}
                      className={cn(
                        'flex items-center gap-4 bg-white border border-gray-200 rounded-lg p-4',
                        'hover:border-blue-300 hover:shadow-md transition-all duration-200',
                        'text-left'
                      )}
                    >
                      {latestProject.avatar_url ? (
                        <img
                          src={latestProject.avatar_url}
                          alt={latestProject.name}
                          className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <Compass className="w-5 h-5 text-blue-600" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-gray-900 truncate">
                          {latestProject.name}
                        </h3>
                        <p className="text-base text-gray-500 truncate">
                          {latestProject.description || 'No description'}
                        </p>
                      </div>
                    </button>
                  </>
                )}

                <button
                  onClick={handleCreateNewProject}
                  className={cn(
                    'inline-flex items-center justify-center gap-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:translate-y-[-2px]',
                    isCreating && 'opacity-75 cursor-not-allowed'
                  )}
                  disabled={isCreating}
                >
                  {isCreating ? <Loader size="large" /> : <FolderPlus className="w-6 h-6" />}
                  <span className="text-lg font-semibold">
                    {hasProjects
                      ? 'New Project'
                      : isCreating
                        ? 'Collecting diamonds...'
                        : "Let's get started!"}
                  </span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <ProjectCreationModal
        isOpen={isNewProjectModalOpen}
        onClose={() => setIsNewProjectModalOpen(false)}
        onSubmit={handleCreateProject}
        isSubmitting={isCreating}
      />
    </div>
  );
}